import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { ApiError, getOrder } from '../api/client'
import { useAuth } from '../auth'
import type { Fulfilment, Nachweis, Order } from '../api/types'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Field } from '../components/Field'
import { Bestellansicht } from '../components/OrderView'
import { PaymentPanel } from '../components/PaymentPanel'
import { useSeo } from '../hooks/useSeo'
import { mitElement, useT } from '../i18n'
import { useShopConfig } from '../shopConfig'

/**
 * Bestätigung und Nachschau in einer Seite.
 *
 * Beides zeigt dieselbe Bestellung — einmal gerade eben aufgegeben, einmal
 * später nachgeschlagen. Zwei Seiten dafür wären zwei Darstellungen derselben
 * Sache, und die zweite veraltet, weil sie seltener angefasst wird.
 *
 * `/bestellung/:nummer` kommt frisch von der Kasse und bringt die Bestellung
 * im Verlaufszustand mit; wer die Adresse später erneut öffnet, sieht das
 * Suchformular mit vorausgefüllter Nummer. Die E-Mail steht nie in der
 * Adresse: Adressen landen in Verläufen, Lesezeichen und Protokollen.
 *
 * Formular **oder** Bestellung, nie beides. Vorher stand die gefundene
 * Bestellung unter dem Suchformular — und damit blieb das Formular stehen,
 * nachdem es seine Arbeit getan hatte, schob die Bestellung nach unten aus dem
 * Bild und lud dazu ein, ein zweites Mal dieselbe Nummer einzutippen. Sobald
 * die Bestellung da ist, gehört ihr die ganze Seite; zurück zur Suche führt ein
 * Knopf.
 */
export function OrderPage() {
  const t = useT()
  const { nummer: nummerAusAdresse } = useParams()
  const { state } = useLocation()
  const { shopName, primaryHost } = useShopConfig()
  const { ich } = useAuth()

  // Was die Kasse mitgibt, ist bereits die fertige Bestellung — ein zweiter
  // Abruf direkt danach brächte nichts Neues und könnte schiefgehen. Die an
  // der Kasse gewählte Zahlungsart kommt mit: Wer dort „Vorkasse" angeklickt
  // hat, soll hier nicht ein zweites Mal wählen müssen.
  const verlauf =
    (state as { bestellung?: Order; email?: string; zahlungsart?: string } | null) ?? null
  const ebenBestellt = verlauf?.bestellung ?? null

  const [bestellung, setBestellung] = useState<Order | null>(ebenBestellt)
  /**
   * Ob diese Bestellung über das Formular gefunden wurde.
   *
   * Nur dann gibt es ein „Zurück": Wer gerade bestellt hat, kam nicht von
   * einer Suche, und ein Knopf, der ihn auf ein leeres Suchformular führt,
   * verspricht einen Rückweg, den es nicht gibt.
   */
  const [ausSuche, setAusSuche] = useState(false)
  const [nummer, setNummer] = useState(nummerAusAdresse ?? '')
  const [email, setEmail] = useState(verlauf?.email ?? '')
  const [beruehrt, setBeruehrt] = useState(false)
  const [abgeschickt, setAbgeschickt] = useState(false)
  const [sucht, setSucht] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const laeuft = useRef(false)

  /**
   * Der Schlüssel aus der Adresse, und was er bewirkt.
   *
   * Steht er da, wird die Bestellung von selbst geholt — ohne Formular, ohne
   * Nummer, ohne E-Mail. Genau das fehlte vorher, und daraus wurden drei
   * Beschwerden: Wer von SumUp zurückkam, landete auf einem leeren
   * Suchformular statt bei seiner bezahlten Bestellung; wer neu lud, verlor
   * sie; und deshalb musste im Text stehen, man möge sich die Nummer merken.
   */
  const [adressfrage] = useSearchParams()
  const token = adressfrage.get('token') ?? ''

  /**
   * Wer angemeldet ist, braucht keinen Schlüssel — er hat seine E-Mail.
   *
   * Damit führt „Meine Bestellungen" auf dieselbe Seite wie alles andere,
   * statt die Bestellung an einer zweiten Stelle in einer zweiten Form zu
   * zeigen. Eine Bestellung, ein Weg, eine Ansicht.
   */
  /**
   * Ob noch zu zahlen ist.
   *
   * Die Bestellung liegt dann zwar im System und die Ware ist zurückgelegt,
   * aber sie ist nicht durch: Bleibt die Zahlung aus, läuft die Reservierung
   * ab und die Bestellung verfällt. Wer an dieser Stelle „Vielen Dank" liest,
   * hält sich für fertig — und wundert sich eine Woche später.
   */
  const offeneZahlung = bestellung?.status === 'awaiting_payment'

  const nachweis: Nachweis | null =
    token !== ''
      ? { token }
      : // Wer über das Formular hierher kam, hat die E-Mail eingegeben — die
        // gilt weiter, auch für das Bezahlen.
        email.trim() !== ''
        ? { email: email.trim() }
        : ich?.email
          ? { email: ich.email }
          : null

  useEffect(() => {
    // Nichts zu tun, wenn die Bestellung schon dasteht — die Kasse gibt sie
    // mit, und ein zweiter Abruf brächte nichts Neues.
    if (bestellung != null || nachweis == null || nummerAusAdresse == null) return

    let abgebrochen = false
    setSucht(true)
    getOrder(nummerAusAdresse, nachweis)
      .then((gefunden) => {
        if (!abgebrochen) setBestellung(gefunden)
      })
      .catch((ausnahme) => {
        if (abgebrochen) return
        // Ein Schlüssel, der nicht passt, ist keine Sackgasse: Das Formular
        // steht darunter, und mit Nummer und E-Mail kommt der Kunde trotzdem
        // an seine Bestellung.
        setFehler(
          ausnahme instanceof ApiError && ausnahme.isNotFound
            ? t('nachschau.linkAbgelaufen')
            : t('nachschau.fehler'),
        )
      })
      .finally(() => {
        if (!abgebrochen) setSucht(false)
      })

    return () => {
      abgebrochen = true
    }
    // Nur beim Öffnen: Der Schlüssel ändert sich nicht, während die Seite steht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Nur beim Öffnen und wenn die Anmeldung steht.
  }, [ich?.email])

  useSeo({
    title: bestellung
      ? t('nachschau.seoTitel', { nummer: bestellung.number, laden: shopName })
      : t('nachschau.seoTitelLeer', { laden: shopName }),
    description: t('nachschau.seoText'),
    canonicalHost: primaryHost,
  })

  async function nachsehen(ereignis: FormEvent) {
    ereignis.preventDefault()
    setAbgeschickt(true)
    setFehler(null)
    if (laeuft.current) return
    if (!nummer.trim() || !email.trim()) return

    laeuft.current = true
    setSucht(true)
    try {
      setBestellung(await getOrder(nummer.trim(), { email: email.trim() }))
      setAusSuche(true)
    } catch (ausnahme) {
      setFehler(
        ausnahme instanceof ApiError && ausnahme.isNotFound
          ? t('nachschau.nichtGefunden')
          : t('nachschau.fehler'),
      )
    } finally {
      laeuft.current = false
      setSucht(false)
    }
  }

  /**
   * Zurück zur Suche, mit leeren Feldern.
   *
   * Leer und nicht mit der letzten Nummer vorausgefüllt: Wer zurückgeht, will
   * eine *andere* Bestellung sehen — die eine, die noch dasteht, hatte er
   * gerade vor Augen.
   */
  function zurueck() {
    setBestellung(null)
    setAusSuche(false)
    setNummer('')
    setEmail('')
    setBeruehrt(false)
    setAbgeschickt(false)
    setFehler(null)
  }

  const krumen = (
    <Breadcrumbs
      pfade={[{ name: t('nav.startseite'), pfad: '/' }, { name: t('nachschau.krume') }]}
    />
  )

  if (!bestellung) {
    return (
      <>
        {krumen}
        <section className="nachschau-karte">
          <h1>{t('nachschau.titel')}</h1>
          <p className="beschreibung">{t('nachschau.text')}</p>

          <form className="nachschau" onSubmit={nachsehen} noValidate>
            <div className="nachschau-felder">
              <Field
                id="nummer"
                label={t('nachschau.nummerLabel')}
                pflicht
                wert={nummer}
                onWert={setNummer}
                onBlur={() => setBeruehrt(true)}
                hinweis={t('nachschau.nummerPlatzhalter')}
                fehler={
                  (beruehrt || abgeschickt) && !nummer.trim() ? t('nachschau.fehlerNummer') : null
                }
              />
              <Field
                id="such-email"
                label={t('nachschau.emailLabel')}
                typ="email"
                inputMode="email"
                autoComplete="email"
                pflicht
                wert={email}
                onWert={setEmail}
                onBlur={() => setBeruehrt(true)}
                fehler={
                  (beruehrt || abgeschickt) && !email.trim() ? t('nachschau.fehlerEmail') : null
                }
              />
            </div>

            <button className="knopf knopf-gross" type="submit" disabled={sucht}>
              {sucht ? t('nachschau.sucht') : t('nachschau.knopf')}
            </button>
          </form>

          {fehler && (
            <p className="hinweis hinweis-fehler" role="alert">
              {fehler}
            </p>
          )}
        </section>
      </>
    )
  }

  return (
    <>
      {krumen}

      {ausSuche && (
        /* Der Rückweg steht über der Bestellung und nicht unter ihr: Wer eine
           andere Nummer nachsehen will, merkt das oben — und nicht, nachdem er
           an fünf Abschnitten vorbeigerollt ist. */
        <p className="nachschau-zurueck">
          <button className="verweisknopf" type="button" onClick={zurueck}>
            ← {t('nachschau.zurueck')}
          </button>
        </p>
      )}

      {ebenBestellt ? (
        <section className="quittung" data-offen={offeneZahlung ? 'ja' : 'nein'}>
          {/* Haken oder Uhr — und das ist kein Zierrat.
              
              Solange die Zahlung aussteht, ist die Bestellung NICHT durch. Ein
              grüner Haken mit „Vielen Dank für Ihre Bestellung" sagte dem
              Kunden das Gegenteil: Er schließt den Tab, und die Ware liegt
              reserviert, bis die Frist abläuft. Genau so war es gemeldet.
              
              Bezahlt oder per Vorkasse bestellt: Haken. Zahlung offen: Uhr,
              und die Überschrift sagt, was noch fehlt. */}
          <svg
            className="quittung-zeichen"
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
            {offeneZahlung ? (
              <path
                d="M12 7v5.2l3.2 2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="m7.5 12.3 3 3 6-6.6"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>

          <h1>{offeneZahlung ? t('nachschau.offenTitel') : t('nachschau.dankeTitel')}</h1>
          <p className="beschreibung">
            {offeneZahlung
              ? t('nachschau.offenText')
              : t('nachschau.dankeText', { laden: shopName })}
          </p>

          <p className="preis-hinweis">
            {/* Die Nummer steht gleich darunter in der Kopfleiste der
                Bestellung, samt Kopierknopf — hier noch einmal groß, und der
                Kunde läse zweimal dasselbe.

                Der Verweis steht mitten im Satz und wird deshalb um ihn herum
                gebaut statt als zweiter Satz angehängt: „Unter Bestellung
                verfolgen nachsehen" ist der Weg, den der Kunde sich merken
                soll. */}
            {mitElement(
              t('nachschau.notieren'),
              '{verweis}',
              <Link to="/bestellung">{t('nav.bestellungVerfolgen')}</Link>,
            )}
          </p>
        </section>
      ) : (
        <h1>{t('nachschau.ihreBestellung')}</h1>
      )}

      {/* Die Zahlung steht vor den Einzelheiten: Sie ist das, was der Kunde
          jetzt tun soll. Die Bestellübersicht kann er danach lesen.

          Welche Arten es gibt, fragt der Abschnitt selbst — die Auswahl hängt
          an der Zustellart dieser Bestellung, nicht an der Einstellung, die
          gerade in der Kasse stand. */}
      {bestellung.status === 'awaiting_payment' && nachweis != null && (
        <PaymentPanel
          nummer={bestellung.number}
          nachweis={nachweis}
          zustellart={zustellartVon(bestellung)}
          vorauswahl={verlauf?.zahlungsart}
          onBezahlt={setBestellung}
        />
      )}

      {/* Die E-Mail reicht die Seite durch: Mit ihr und der Nummer steht der
          Verweis auf die Rechnung da. Ohne sie — etwa nach einem Neuladen mit
          leerem Verlaufszustand — bleibt er weg statt ins Leere zu führen. */}
      <Bestellansicht bestellung={bestellung} email={email.trim() || undefined} />
    </>
  )
}

/**
 * Die Zustellart der Bestellung, für die Auswahl der Zahlungsarten.
 *
 * Alles außer „pickup" gilt als Versand: Ein unbekannter Wert darf nicht dazu
 * führen, dass jemand Barzahlung bei Abholung angeboten bekommt, obwohl das
 * Paket unterwegs ist.
 */
function zustellartVon(bestellung: Order): Fulfilment {
  return bestellung.fulfilment === 'pickup' ? 'pickup' : 'shipping'
}
